import { useEffect, useRef, useState } from "react";

const ITEMS = '[role="menu"] [role^="menuitem"]:not(:disabled)';

/**
 * A menu opened by a button (a link's "⋯", a filter): opening moves the focus to its first item, the arrows move it
 * between items, Esc closes and gives the focus back to the button, and a click outside closes. `ref` goes on the
 * element holding the button (`aria-haspopup="menu"`) and the menu (`role="menu"` with `role="menuitem*"` items).
 */
export const useMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const items = () => Array.from(ref.current?.querySelectorAll<HTMLElement>(ITEMS) ?? []);
    items()[0]?.focus();

    const onMouseDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]')?.focus();
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const list = items();
        if (!list.length || !ref.current?.contains(document.activeElement)) return;
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        const index = list.indexOf(document.activeElement as HTMLElement);
        list[(index + step + list.length) % list.length].focus();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return { open, setOpen, ref };
};
